import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, HeartOff, ImageOff, LoaderCircle, ShoppingBag } from "lucide-react";
import { formatINR, getProductCategories, resolveProductImage } from "../../lib/utils-cynos";

const normalizeOptions = (options = [], selected = "") => {
  const cleaned = Array.from(new Set((Array.isArray(options) ? options : []).filter(Boolean)));
  if (selected && !cleaned.some((option) => String(option).toLowerCase() === String(selected).toLowerCase())) {
    cleaned.unshift(selected);
  }
  return cleaned;
};

const selectedImageFor = (product, selectedColor) => {
  const colorImageKey = Object.keys(product?.color_images || {}).find(
    (key) => key.toLowerCase() === String(selectedColor || "").toLowerCase()
  );
  return resolveProductImage((colorImageKey && product.color_images[colorImageKey]?.[0]) || product?.images?.[0]);
};

const productDescription = (product) =>
  product?.short_description ||
  product?.description ||
  product?.subtitle ||
  getProductCategories(product).join(" / ") ||
  "Saved for your next CYNOS drop.";

const VariantSelect = ({ label, value, options, disabled, onChange }) => {
  const values = normalizeOptions(options, value);

  return (
    <label className="min-w-0">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
        {label}
      </span>
      <select
        aria-label={label}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || values.length === 0}
        className="h-11 w-full rounded-[10px] border border-white/10 bg-[#202020] px-2 text-[11px] font-semibold uppercase text-white outline-none transition focus:border-cynos-red focus:ring-2 focus:ring-cynos-red/20 disabled:cursor-not-allowed disabled:opacity-60 min-[390px]:px-3 min-[390px]:text-xs"
      >
        {values.length === 0 && <option value="">--</option>}
        {values.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
};

const WishlistItem = ({ item, onRemove, onMoveToCart, onVariantChange, moving }) => {
  const product = item.product;
  const unavailable = !product;
  const image = selectedImageFor(product, item.selectedColor);
  const outOfStock = Number(product?.stock_count || 0) <= 0;
  const disabled = unavailable || moving;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.992 }}
      className="overflow-hidden rounded-[18px] border border-white/10 bg-[#181818] p-4 shadow-[0_18px_55px_rgba(0,0,0,0.28)] transition hover:border-white/20 hover:bg-white/[0.045]"
      data-testid="wishlist-item"
    >
      <div className="grid grid-cols-[minmax(0,1.42fr)_minmax(0,1fr)] gap-3 md:block">
        <Link
          to={product?.slug ? `/product/${product.slug}` : "#"}
          className="group relative h-[276px] overflow-hidden rounded-[16px] border border-white/10 bg-black md:block md:h-[360px]"
          aria-label={product?.name ? `View ${product.name}` : "Unavailable wishlist item"}
          tabIndex={unavailable ? -1 : 0}
          onClick={(event) => {
            if (unavailable) event.preventDefault();
          }}
        >
          <ImageOff className="absolute inset-0 m-auto h-6 w-6 text-zinc-700" aria-hidden="true" />
          {image && (
            <img
              src={image}
              alt={product?.name || "Wishlist item"}
              loading="lazy"
              className="relative z-10 h-full w-full object-cover object-center transition duration-500 group-hover:scale-[1.035]"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          )}
          <div className="absolute inset-x-0 bottom-0 z-20 flex items-center justify-between bg-gradient-to-t from-black/80 via-black/25 to-transparent p-3 opacity-0 transition group-hover:opacity-100">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white">View Drop</span>
            <ArrowUpRight size={16} className="text-white" />
          </div>
        </Link>

        <div className="flex h-[276px] min-w-0 flex-col md:h-auto md:pt-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-start justify-between gap-2">
              {unavailable ? (
                <h3 className="line-clamp-2 min-w-0 text-[16px] font-extrabold uppercase leading-[1.08] text-zinc-400 min-[390px]:text-[17px]">
                  Product unavailable
                </h3>
              ) : (
                <Link
                  to={`/product/${product.slug}`}
                  className="line-clamp-2 min-w-0 text-[16px] font-extrabold uppercase leading-[1.08] text-white transition hover:text-cynos-red min-[390px]:text-[17px]"
                >
                  {product.name}
                </Link>
              )}

              {product && (
                <span className="shrink-0 text-right text-xl font-black leading-none text-cynos-red">
                  {formatINR(product.price)}
                </span>
              )}
            </div>

            <p className="line-clamp-2 text-xs leading-relaxed text-zinc-400">
              {unavailable ? "This saved drop is no longer available." : productDescription(product)}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {!unavailable && (
                <span
                  className={`rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] ${
                    outOfStock
                      ? "border-cynos-red/30 bg-cynos-red/10 text-cynos-red"
                      : "border-emerald-400/25 bg-emerald-400/10 text-emerald-300"
                  }`}
                >
                  {outOfStock ? "Out of stock" : "In stock"}
                </span>
              )}
              {!item.selectedSize && !item.selectedColor && (
                <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-400">
                  Default
                </span>
              )}
            </div>
          </div>

          {product && product.original_price > product.price && (
            <div className="mt-2 text-xs text-zinc-500 line-through">{formatINR(product.original_price)}</div>
          )}

          <div className="mt-auto grid grid-cols-2 gap-2">
            <VariantSelect
              label="Size"
              value={item.selectedSize || ""}
              options={product?.sizes || []}
              disabled={disabled}
              onChange={(nextSize) => onVariantChange(item, nextSize, item.selectedColor || "")}
            />
            <VariantSelect
              label="Color"
              value={item.selectedColor || ""}
              options={product?.colors || []}
              disabled={disabled}
              onChange={(nextColor) => onVariantChange(item, item.selectedSize || "", nextColor)}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onMoveToCart(item)}
          disabled={unavailable || outOfStock || moving}
          className="inline-flex h-12 items-center justify-center gap-2 rounded-[12px] bg-white px-3 font-mono text-[10px] font-black uppercase tracking-[0.12em] text-black transition hover:bg-cynos-red hover:text-white active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 min-[390px]:text-xs"
        >
          {moving ? <LoaderCircle size={15} className="animate-spin" /> : <ShoppingBag size={15} />}
          {moving ? "Moving" : "Move To Cart"}
        </button>

        <button
          type="button"
          onClick={() => onRemove(item)}
          disabled={moving}
          className="inline-flex h-12 items-center justify-center gap-2 rounded-[12px] border border-white/15 bg-[#202020] px-3 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-200 transition hover:border-cynos-red hover:bg-cynos-red/10 hover:text-cynos-red active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 min-[390px]:text-xs"
        >
          <HeartOff size={15} />
          Remove
        </button>
      </div>
    </motion.article>
  );
};

export default WishlistItem;
